import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import {
  Building2,
  Search,
  ChevronRight,
  BarChart3,
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export const Dependencias = () => {
  const [dependencies, setDependencies] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [sortBy, setSortBy] = useState('name'); // 'name', 'beneficiarios', 'activities'

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [depsRes, statsRes] = await Promise.all([
          axios.get(`${API}/dependencies`),
          axios.get(`${API}/stats/by-dependency`),
        ]);

        setDependencies(Array.isArray(depsRes.data) ? depsRes.data : []);
        setStats(statsRes.data || {});
      } catch (error) {
        console.error('Error fetching dependencies:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const filteredDependencies = useMemo(() => {
    return dependencies
      .filter((dep) => {
        const name = dep.name?.toLowerCase() || '';
        const acronym = dep.acronym?.toLowerCase() || '';
        const query = searchQuery.toLowerCase();

        return name.includes(query) || acronym.includes(query);
      })
      .sort((a, b) => {
        const statsA = stats[a.id] || {};
        const statsB = stats[b.id] || {};

        switch (sortBy) {
          case 'beneficiarios':
            return (statsB.total_beneficiarios || 0) - (statsA.total_beneficiarios || 0);
          case 'activities':
            return (statsB.total_activities || 0) - (statsA.total_activities || 0);
          default:
            return (a.name || '').localeCompare(b.name || '');
        }
      });
  }, [dependencies, stats, searchQuery, sortBy]);

  const chartData = useMemo(() => {
    return Object.values(stats)
      .filter((item) => (item.total_beneficiarios || 0) > 0)
      .sort((a, b) => (b.total_beneficiarios || 0) - (a.total_beneficiarios || 0))
      .slice(0, 10)
      .map((item) => ({
        name: item.dependency?.acronym || 'N/A',
        beneficiarios: item.total_beneficiarios || 0,
        actividades: item.total_activities || 0,
      }));
  }, [stats]);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#F5F0E1]">
        <div className="animate-pulse flex flex-col items-center gap-4">
          <div className="w-16 h-16 rounded-full bg-[#8B1C4A]/20" />
          <p className="text-slate-600">Cargando dependencias...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F5F0E1]" data-testid="dependencias-page">
      <section className="bg-white border-b border-[#D4C5A5]/30 py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row lg:items-center justify-between gap-4">
            <div>
              <h1 className="text-3xl lg:text-4xl font-bold text-slate-800 tracking-tight">
                {dependencies.length} Dependencias del Gobierno
              </h1>
              <p className="text-slate-600 mt-2">
                Conoce el trabajo de cada institución en los territorios
              </p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 mt-6">
            <div className="relative flex-1 max-w-md">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                placeholder="Buscar dependencia..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-10"
                data-testid="search-dependency"
              />
            </div>

            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-slate-500">Ordenar:</span>

              <Button
                variant={sortBy === 'name' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSortBy('name')}
                className={sortBy === 'name' ? 'bg-[#8B1C4A] hover:bg-[#70163A]' : ''}
              >
                Nombre
              </Button>

              <Button
                variant={sortBy === 'beneficiarios' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSortBy('beneficiarios')}
                className={sortBy === 'beneficiarios' ? 'bg-[#8B1C4A] hover:bg-[#70163A]' : ''}
              >
                Beneficiarios
              </Button>

              <Button
                variant={sortBy === 'activities' ? 'default' : 'outline'}
                size="sm"
                onClick={() => setSortBy('activities')}
                className={sortBy === 'activities' ? 'bg-[#8B1C4A] hover:bg-[#70163A]' : ''}
              >
                Actividades
              </Button>
            </div>
          </div>
        </div>
      </section>

      {chartData.length > 0 && (
        <section className="py-8 bg-white border-b border-[#D4C5A5]/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <Card className="border border-[#D4C5A5]/30">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <BarChart3 className="w-5 h-5 text-[#8B1C4A]" />
                  Top 10 Dependencias por Impacto
                </CardTitle>
              </CardHeader>

              <CardContent>
                <div className="h-[300px]" data-testid="dependencies-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={chartData}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#D4C5A5" />
                      <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: 'white',
                          border: '1px solid #D4C5A5',
                          borderRadius: '8px',
                        }}
                      />
                      <Bar
                        dataKey="beneficiarios"
                        fill="#8B1C4A"
                        name="Beneficiarios"
                        radius={[4, 4, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>
      )}

      <section className="py-8">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="dependencies-grid">
            {filteredDependencies.map((dependency) => {
              const depStats = stats[dependency.id] || {};

              return (
                <Link
                  key={dependency.id}
                  to={`/dependencia/${dependency.id}`}
                  data-testid={`dependency-card-${dependency.id}`}
                >
                  <Card className="h-full transition-all duration-300 hover:shadow-lg hover:-translate-y-1 border border-[#D4C5A5]/30">
                    <CardContent className="p-5">
                      <div className="flex items-start gap-4">
                        <div
                          className="p-3 rounded-lg shrink-0"
                          style={{ backgroundColor: `${dependency.color}20` }}
                        >
                          <Building2
                            className="w-6 h-6"
                            style={{ color: dependency.color }}
                          />
                        </div>

                        <div className="flex-1 min-w-0">
                          <Badge
                            variant="secondary"
                            className="mb-2"
                            style={{
                              backgroundColor: `${dependency.color}20`,
                              color: dependency.color,
                            }}
                          >
                            {dependency.acronym}
                          </Badge>

                          <h3 className="font-semibold text-slate-800 line-clamp-2 leading-tight">
                            {dependency.name}
                          </h3>
                        </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4 mt-4 pt-4 border-t border-slate-100">
                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">
                            Beneficiarios
                          </p>
                          <p className="text-xl font-bold text-[#8B1C4A]">
                            {(depStats.total_beneficiarios || 0).toLocaleString()}
                          </p>
                        </div>

                        <div>
                          <p className="text-xs text-slate-500 uppercase tracking-wide">
                            Actividades
                          </p>
                          <p className="text-xl font-bold text-[#BC955C]">
                            {depStats.total_activities || 0}
                          </p>
                        </div>
                      </div>

                      <div className="flex items-center justify-end mt-4 text-sm text-[#8B1C4A] font-medium">
                        Ver detalle
                        <ChevronRight className="w-4 h-4 ml-1" />
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>

          {filteredDependencies.length === 0 && (
            <div className="text-center py-12">
              <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-4" />
              <p className="text-slate-500">No se encontraron dependencias</p>
            </div>
          )}
        </div>
      </section>
    </div>
  );
};

export default Dependencias;